<script setup>
/**
 * How one piece of wording actually PERFORMED — the half of this screen that is about results
 * rather than permission.
 *
 * Everything else here answers "may this be sent". Even the quality rating, which looks like a
 * result, is a complaint metric: it moves when customers block or report, and says nothing about
 * whether anyone read the message. A business that sends a marketing template wants one number,
 * and it is the read rate.
 *
 * ── THE SEVEN-DAY WINDOW IS THE PRODUCT DECISION ────────────────────────────────────
 *
 * Meta keeps read and click counts for seven days from the send and then RESETS THEM TO ZERO. So
 * a 30-day read rate is not stale, it is WRONG — it reports zero reads for messages that were
 * read. Seven days is the only window in which all four numbers mean what they appear to mean,
 * and the panel says so rather than leaving an owner to wonder why last month looks dead.
 *
 * ── LOADED ON DEMAND, NOT ON RENDER ─────────────────────────────────────────────────
 *
 * Meta allows ten template ids per request; a list of twenty-five rows that each fetched on open
 * would be twenty-five round trips behind a screen that is already reading the library. So this
 * asks only when the owner asks.
 *
 * ── THREE REFUSALS, THREE DIFFERENT SCREENS ─────────────────────────────────────────
 *
 * Meta publishes two error codes that mean opposite things, and collapsing them is how an owner
 * ends up pressing a switch that cannot work:
 *   200007  not switched on yet — she can fix it, and the switch is offered WITH Meta's own
 *           consent wording, because turning it on is irreversible and it directs Meta to collect
 *           and anonymise data from her chats. That sentence is hers to read before she agrees.
 *   200005  Meta will not offer analytics for this account (it excludes business accounts in the
 *           EU and Japan). No switch, because there is nothing to switch.
 *   anything else — Meta's own sentence, forwarded.
 */
import { computed, ref } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

import { useWasloI18n } from '../i18n.js';
import { templatesApi } from '../api/templates.js';
import { formatCount } from '../helpers/plural.js';

const props = defineProps({
  accountId: { type: Number, default: null },
  inboxId: { type: String, default: '' },
  /** The narrowed row. `ctaUrlLinkTrackingOptedOut` explains an absent click count. */
  template: { type: Object, required: true },
});

const { t, locale } = useWasloI18n();

const isLoading = ref(false);
const report = ref(null);
const failure = ref('');
const notEnabled = ref(false);
const unavailable = ref(false);
const isEnabling = ref(false);

const load = async () => {
  if (isLoading.value || props.accountId === null || !props.template.id) return;
  isLoading.value = true;
  failure.value = '';
  notEnabled.value = false;
  unavailable.value = false;
  try {
    report.value = await templatesApi.analytics(
      props.accountId,
      props.inboxId,
      props.template.id
    );
  } catch (error) {
    const meta = error?.metaError ?? {};
    notEnabled.value = meta.not_enabled === true;
    unavailable.value = meta.unavailable === true;
    // Meta's own sentence whenever Meta wrote one. This endpoint's refusals are the kind an owner
    // can act on, and a paraphrase of ours would lose the part that says which account.
    failure.value =
      notEnabled.value || unavailable.value
        ? ''
        : meta.message || t('WASLO.TEMPLATES.ANALYTICS.FAILED');
  } finally {
    isLoading.value = false;
  }
};

const enable = async () => {
  if (isEnabling.value) return;
  isEnabling.value = true;
  failure.value = '';
  try {
    await templatesApi.enableInsights(props.accountId, props.inboxId);
    notEnabled.value = false;
    await load();
  } catch (error) {
    failure.value =
      error?.metaError?.message || t('WASLO.TEMPLATES.ANALYTICS.ENABLE_FAILED');
  } finally {
    isEnabling.value = false;
  }
};

/** A percentage only when both halves are real numbers — never a rate out of a missing total. */
const rate = (part, whole) => {
  if (typeof part !== 'number' || typeof whole !== 'number' || whole === 0) {
    return '';
  }
  return `${Math.round((part / whole) * 100)}%`;
};

const tiles = computed(() => {
  const given = report.value;
  if (!given) return [];
  return [
    { key: 'SENT', value: given.sent, note: '' },
    {
      key: 'DELIVERED',
      value: given.delivered,
      note: rate(given.delivered, given.sent),
    },
    { key: 'READ', value: given.read, note: rate(given.read, given.delivered) },
  ];
});

/**
 * Clicks, already grouped by Meta's own `type` in `api/templates.js` and NEVER summed across
 * types — `url_button` is the raw count and `unique_url_button` is the same taps deduplicated per
 * account, so adding them double-counts.
 */
const clickLabel = click => {
  const key = `WASLO.TEMPLATES.ANALYTICS.CLICK.${click.type.toUpperCase()}`;
  const sentence = t(key);
  return sentence === key ? click.type : sentence;
};

// Meta returns no clicks for an authentication template, and none at all once the business turns
// link tracking off — both of which look identical to "nobody tapped" unless they are said.
const clicksExplained = computed(() => {
  if (props.template.ctaUrlLinkTrackingOptedOut === true) {
    return t('WASLO.TEMPLATES.ANALYTICS.TRACKING_OFF');
  }
  if ((props.template.category ?? '').toUpperCase() === 'AUTHENTICATION') {
    return t('WASLO.TEMPLATES.ANALYTICS.NO_CLICKS_AUTH');
  }
  return '';
});
</script>

<template>
  <div class="flex flex-col gap-3" data-waslo-template-analytics>
    <p class="mb-0 text-xs text-n-slate-10">
      {{ t('WASLO.TEMPLATES.ANALYTICS.HEADING') }}
    </p>

    <div
      v-if="!report && !isLoading && !notEnabled && !unavailable"
      class="flex"
    >
      <Button
        :label="t('WASLO.TEMPLATES.ANALYTICS.LOAD')"
        icon="i-lucide-bar-chart-3"
        variant="faded"
        color="slate"
        size="sm"
        data-waslo-template-analytics-load
        @click="load"
      />
    </div>

    <div
      v-if="isLoading"
      class="flex items-center gap-2 text-sm text-n-slate-11"
      data-waslo-template-analytics-loading
    >
      <Spinner />
    </div>

    <!--
      NOT SWITCHED ON — and this is the one switch on the screen that is irreversible AND consents
      to something on the business's behalf, so Meta's own wording sits above it rather than a
      cheerful paraphrase of ours.
    -->
    <div
      v-if="notEnabled"
      class="flex flex-col gap-2 px-4 py-3 rounded-xl bg-n-solid-2"
      data-waslo-template-analytics-off
    >
      <p class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11">
        {{ t('WASLO.TEMPLATES.ANALYTICS.OFF') }}
      </p>
      <p class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11">
        {{ t('WASLO.TEMPLATES.ANALYTICS.CONSENT') }}
      </p>
      <div class="flex">
        <Button
          :label="
            isEnabling
              ? t('WASLO.TEMPLATES.ANALYTICS.ENABLING')
              : t('WASLO.TEMPLATES.ANALYTICS.ENABLE')
          "
          variant="faded"
          color="slate"
          size="sm"
          :is-loading="isEnabling"
          :disabled="isEnabling"
          data-waslo-template-analytics-enable
          @click="enable"
        />
      </div>
    </div>

    <!-- Meta will not offer analytics for this account at all. No switch: there is nothing to
         switch, and offering one would be a button that fails every time. -->
    <p
      v-if="unavailable"
      class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11"
      data-waslo-template-analytics-unavailable
    >
      {{ t('WASLO.TEMPLATES.ANALYTICS.UNAVAILABLE') }}
    </p>

    <p
      v-if="failure"
      class="mb-0 text-sm leading-6 max-w-[62ch] text-n-ruby-9"
      dir="auto"
      data-waslo-template-analytics-error
    >
      <bdi>{{ failure }}</bdi>
    </p>

    <template v-if="report">
      <!-- Nothing sent in the window is a real answer, not a failure. -->
      <p
        v-if="report.empty"
        class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11"
        data-waslo-template-analytics-empty
      >
        {{ t('WASLO.TEMPLATES.ANALYTICS.EMPTY', { count: report.days }) }}
      </p>

      <template v-else>
        <div class="grid gap-3 sm:grid-cols-3">
          <div
            v-for="tile in tiles"
            :key="tile.key"
            class="flex flex-col gap-0.5 px-4 py-3 rounded-xl bg-n-solid-2"
            :data-waslo-template-metric="tile.key"
          >
            <span class="text-xs text-n-slate-10">
              {{ t(`WASLO.TEMPLATES.ANALYTICS.${tile.key}`) }}
            </span>
            <!--
              A MISSING METRIC IS NOT A ZERO. Meta omits a metric it did not send, and "WhatsApp
              did not tell us" is a different fact from "it happened zero times".
            -->
            <span class="text-lg font-medium text-n-slate-12">
              {{
                typeof tile.value === 'number'
                  ? tile.value
                  : t('WASLO.TEMPLATES.FACTS.UNTOLD')
              }}
            </span>
            <span v-if="tile.note" class="text-xs text-n-slate-10">
              {{ tile.note }}
            </span>
          </div>
        </div>

        <ul
          v-if="report.clicks.length"
          class="flex flex-col gap-1 mb-0 list-none"
          data-waslo-template-analytics-clicks
        >
          <li
            v-for="(click, index) in report.clicks"
            :key="index"
            class="text-sm text-n-slate-11"
            dir="auto"
          >
            <span class="text-n-slate-10">{{ clickLabel(click) }}</span>
            <bdi v-if="click.label"> · {{ click.label }}</bdi>
            <span class="font-medium text-n-slate-12">
              · {{ formatCount(click.count, locale) }}
            </span>
          </li>
        </ul>
        <p
          v-else-if="clicksExplained"
          class="mb-0 text-xs leading-5 max-w-[62ch] text-n-slate-10"
          data-waslo-template-analytics-no-clicks
        >
          {{ clicksExplained }}
        </p>
      </template>

      <!--
        The seven-day reset, stated where the numbers are. Without it an owner reads last month's
        zero reads as a product that stopped working.
      -->
      <p class="mb-0 text-xs leading-5 max-w-[62ch] text-n-slate-10">
        {{ t('WASLO.TEMPLATES.ANALYTICS.WINDOW', { count: report.days }) }}
      </p>
    </template>
  </div>
</template>
